import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { BillDTO, PaymentMethodFilter, PaymentsResponse } from '@six-pockets/shared';
import { billsApi } from '@/api';
import { SearchIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { DatePicker, InlineAlert, Pagination, SegmentedControl, TextField, type SegmentOption } from '@/components/ui';
import { BillDetailsModal } from '@/features/bills/components/BillDetailsModal';
import { PaymentRow } from '@/features/bills/components/PaymentRow';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDayLabel, todayKey } from '@/utils/date';
import { formatINR } from '@/utils/format';
import styles from '@/styles/pages/PaymentsPage/style.scss';

const PAGE_SIZE = 12;

type MethodFilter = 'all' | PaymentMethodFilter;

const METHOD_OPTIONS: SegmentOption<MethodFilter>[] = [
  { value: 'all', label: 'All' },
  { value: 'upi', label: 'UPI' },
  { value: 'cash', label: 'Cash' },
];

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

/**
 * Bills → Payments (admin): every payment received on a day (today by default), newest first, as the customer made
 * it, so the drawer and the UPI app can be matched entry by entry. All | UPI | Cash (a split shows under both),
 * search (customer, mobile, bill number, a name on the bill) and paging, all done by the server. The cards on top
 * cover the whole day. View bill opens the bill it went to.
 */
export function PaymentsPage() {
  // The day and the method live in the URL (?method=upi&date=), so Reports → Collection can link straight here.
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? todayKey();
  const m = params.get('method');
  const method: MethodFilter = m === 'upi' || m === 'cash' ? m : 'all';
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 300);
  const [page, setPage] = useState(1);

  const [result, setResult] = useState<PaymentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<BillDTO | null>(null);

  // A new search goes back to the first page.
  useEffect(() => setPage(1), [query]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    billsApi.payments({ date, method: method === 'all' ? undefined : method, q: query || undefined, page, pageSize: PAGE_SIZE }, ctrl.signal).then(
      (r) => {
        if (r.items.length === 0 && r.page > 1) setPage(r.page - 1);
        else setResult(r);
        setError(null);
        setLoading(false);
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(messageOf(err));
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [date, method, query, page]);

  const setFilters = (next: { date: string; method: MethodFilter }) => {
    setParams(next.method === 'all' ? { date: next.date } : { method: next.method, date: next.date }, { replace: true });
    setPage(1);
  };
  const changeMethod = (method: MethodFilter) => setFilters({ date, method });
  const changeDate = (d: string) => setFilters({ date: d, method });

  const day = formatDayLabel(date);
  const payments = result?.items ?? [];
  const summary = result?.summary;

  return (
    <>
      <PageHeader title="Payments" />

      {summary && (
        <section className={styles.summary} aria-label="Payments received">
          <div className={styles.received}>
            <span className={styles.statLabel}>Received {day === 'Today' || day === 'Yesterday' ? day.toLowerCase() : `on ${day}`}</span>
            <span className={styles.receivedValue}>{formatINR(summary.total)}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>UPI</span>
            <span className={styles.statValue}>{formatINR(summary.upi)}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Cash</span>
            <span className={styles.statValue}>{formatINR(summary.cash)}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Payments</span>
            <span className={styles.statValue}>{summary.count}</span>
          </div>
        </section>
      )}

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <SegmentedControl legend="Payment mode" hideLegend columns={3} options={METHOD_OPTIONS} value={method} onChange={changeMethod} />
        </div>
        <div className={styles.tools}>
          <TextField
            size="md"
            aria-label="Search payments by customer, mobile or bill number"
            placeholder="Search customer, mobile, bill #"
            prefix={<SearchIcon size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.search}
          />
          <div className={styles.date}>
            <DatePicker label="Day paid" value={date} onChange={changeDate} />
          </div>
        </div>
      </div>

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {!result && loading ? (
        <p className={styles.empty}>Loading payments…</p>
      ) : payments.length === 0 ? (
        <p className={styles.empty}>
          {query ? 'No payments match your search' : method === 'all' ? 'No payments on this day' : `No ${method === 'upi' ? 'UPI' : 'cash'} payments on this day`}
        </p>
      ) : (
        <ul className={styles.list} aria-busy={loading}>
          {payments.map((p) => (
            <PaymentRow key={p.id} payment={p} onView={setViewing} />
          ))}
        </ul>
      )}

      {result && <Pagination page={result.page} pageSize={result.pageSize} total={result.total} onPageChange={setPage} />}

      {viewing && <BillDetailsModal key={viewing.id} bill={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
